import { apiService } from './api';
import { unwrapResponse } from './apiUtils';
import {
  Farmer,
  CreateFarmerDto,
  UpdateFarmerDto,
  FarmerListRequest,
  FarmerListResponse
} from '../types/farmers';
import { FilterOption } from '../types/options';
import { EntityDependencySummary } from '../types/entityDependencies';
import { DuplicateMatch } from '../types/duplicateMatch';

export const farmerService = {
  // Slim { id, name } farmer options for filter dropdowns (name = "First Surname"), ordered by name.
  // Cacheable on the server (private, max-age=300 + ETag/304); avoids the heavy paginated list fetch
  // when a screen only needs to populate a farmer filter.
  getOptions: async (): Promise<FilterOption[]> => {
    const response = await apiService.get<FilterOption[]>('/farmers/options');

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farmer options');
    }

    return response.data;
  },

  // Get paginated farmers with search and filtering
  getFarmers: async (request: FarmerListRequest): Promise<FarmerListResponse> => {
    const params = new URLSearchParams();

    if (request.pageNumber) params.append('pageNumber', request.pageNumber.toString());
    if (request.pageSize) params.append('pageSize', request.pageSize.toString());
    if (request.searchTerm) params.append('searchTerm', request.searchTerm);
    if (request.sortBy) params.append('sortBy', request.sortBy);
    if (request.sortDescending !== undefined) params.append('sortDescending', request.sortDescending.toString());

    const response = await apiService.get<FarmerListResponse>(`/farmers?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farmers');
    }

    return response.data;
  },

  // Get farmer by ID
  getById: async (id: number): Promise<Farmer> => {
    const response = await apiService.get<Farmer>(`/farmers/${id}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farmer');
    }

    return response.data;
  },

  // Create new farmer
  create: async (data: CreateFarmerDto): Promise<Farmer> => {
    const response = await apiService.post<Farmer>('/farmers', data);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to create farmer');
    }

    return response.data;
  },

  // Update farmer
  update: async (id: number, data: UpdateFarmerDto): Promise<Farmer> => {
    const response = await apiService.put<Farmer>(`/farmers/${id}`, data);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update farmer');
    }

    return response.data;
  },

  // Delete farmer
  delete: async (id: number): Promise<void> => {
    const response = await apiService.delete<void>(`/farmers/${id}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete farmer');
    }
  },

  // Delete pre-check: what references this farmer + whether it can be deleted (server is authoritative).
  getDependencies: async (id: number): Promise<EntityDependencySummary> => {
    const response = await apiService.get<EntityDependencySummary>(`/farmers/${id}/dependencies`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to check farmer dependencies');
    }

    return response.data;
  },

  // Search farmers
  search: async (searchTerm: string): Promise<Farmer[]> => {
    const response = await apiService.get<Farmer[]>(`/farmers/search?searchTerm=${encodeURIComponent(searchTerm)}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to search farmers');
    }

    return response.data;
  },

  // Create-time "did you mean?" nudge (Plan C, Layer 1): farmers matching the in-progress email/phone/name.
  // Non-blocking — see useDuplicateCheck / DuplicateMatchPanel. Query built from defined params only.
  checkDuplicates: async (params: { email?: string; phone?: string; name?: string; excludeId?: number }): Promise<DuplicateMatch[]> => {
    const query = new URLSearchParams();
    if (params.email) query.set('email', params.email);
    if (params.phone) query.set('phone', params.phone);
    if (params.name) query.set('name', params.name);
    if (params.excludeId != null) query.set('excludeId', String(params.excludeId));

    const response = await apiService.get<DuplicateMatch[]>(`/farmers/check-duplicates?${query.toString()}`);
    return unwrapResponse(response, 'Failed to check for duplicate farmers');
  },
};
